import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Buildings, GridFour, Database, Hash, ChatCircle, CaretRight, Cube } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import TreeView from "@/components/TreeNode";

function StatCard({ label, value, accent = false, testid }) {
  return (
    <div
      className={`p-6 border transition-all duration-200 ${
        accent
          ? "bg-slate-900 text-white border-slate-900 hover:border-orange-600"
          : "card-sharp"
      }`}
      data-testid={testid}
    >
      <div className={`mono-label ${accent ? "text-slate-400" : ""}`}>{label}</div>
      <div className={`font-display text-4xl mt-2 ${accent ? "text-white" : "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}

function MiniStat({ label, value, note }) {
  return (
    <div className="border border-slate-200 px-4 py-3 bg-slate-50">
      <div className="mono-label">{label}</div>
      <div className="font-mono text-2xl text-slate-900 mt-1">{value}</div>
      <div className="mono-label text-slate-400 mt-1">{note}</div>
    </div>
  );
}

const TYPE_META = {
  LOBBY: { icon: Buildings, color: "text-orange-600", label: "Lobby" },
  SECTOR: { icon: GridFour, color: "text-blue-600", label: "Sector" },
  BLOCK: { icon: Cube, color: "text-emerald-600", label: "Block" },
  BUILDING: { icon: Buildings, color: "text-purple-600", label: "Building" },
  FLAT: { icon: Hash, color: "text-slate-600", label: "Flat" },
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [tree, setTree] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/tree").then((r) => setTree(r.data));
  }, []);

  return (
    <div className="space-y-10" data-testid="admin-dashboard">
      <div>
        <div className="mono-label text-orange-600 mb-2">// system overview</div>
        <h1 className="font-display text-4xl md:text-5xl text-slate-900 leading-tight tracking-tight">
          Welcome to the<br />Control Room
        </h1>
        <p className="text-slate-600 mt-3 max-w-2xl">
          Live structural map of the 3CCTech.ITC.AiVerse knowledge complex. All addresses,
          all residents, all the time.
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Knowledge" value={stats?.knowledge_count ?? "—"} accent testid="stat-knowledge" />
        <StatCard label="Locations" value={stats?.locations_count ?? "—"} testid="stat-locations" />
        <StatCard label="Hashtags" value={stats?.hashtags_count ?? "—"} testid="stat-hashtags" />
        <StatCard label="Queries Served" value={stats?.queries_count ?? "—"} testid="stat-queries" />
      </div>

      {/* Ingestion graph stats */}
      <div className="border border-slate-200 bg-white p-5" data-testid="ingestion-stats">
        <div className="mono-label mb-3 flex items-center gap-2">
          <span className="text-orange-600">// world-class ingestion · live counts</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="Chunks" value={stats?.chunks_count ?? "—"} note="post-overlap" />
          <MiniStat label="Entities" value={stats?.entities_count ?? "—"} note="deduped graph nodes" />
          <MiniStat label="Relationships" value={stats?.relationships_count ?? "—"} note="graph edges" />
          <MiniStat label="Vector index" value={stats?.vector_index_size ?? "—"} note="in-memory · ms search" />
        </div>
      </div>

      {/* Type breakdown */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {Object.entries(stats.by_type).map(([type, count]) => {
            const meta = TYPE_META[type] || {};
            const Icon = meta.icon || Buildings;
            return (
              <div key={type} className="border border-slate-200 px-4 py-3 flex items-center gap-3 bg-white">
                <Icon size={18} className={meta.color} />
                <div>
                  <div className="mono-label">{meta.label}</div>
                  <div className="font-mono text-lg text-slate-900">{count}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tree map */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-slate-200 bg-white">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="mono-label">Complex Tree</div>
              <div className="font-display text-xl text-slate-900">3CCTech.ITC.AiVerse</div>
            </div>
            <Link to="/admin/locations" className="btn-sharp btn-secondary text-sm" data-testid="manage-locations-link">
              Manage map <CaretRight size={14} />
            </Link>
          </div>
          <div className="max-h-[520px] overflow-y-auto" data-testid="complex-tree">
            <TreeView roots={tree} />
          </div>
        </div>

        <div className="space-y-4">
          <Link to="/admin/data-entry" className="block card-sharp p-6 group" data-testid="quick-add-knowledge">
            <Database size={28} className="text-orange-600 mb-4" />
            <div className="font-display text-xl text-slate-900 mb-1">Add Knowledge</div>
            <div className="text-sm text-slate-600">
              Drop content into any flat. Use voice, text, or attachments.
            </div>
            <div className="mt-4 mono-label flex items-center gap-1 text-orange-600 group-hover:gap-2 transition-all">
              Start entry <CaretRight size={12} />
            </div>
          </Link>
          <Link to="/admin/hashtags" className="block card-sharp p-6 group" data-testid="quick-hashtags">
            <Hash size={28} className="text-slate-700 mb-4" />
            <div className="font-display text-xl text-slate-900 mb-1">Hashtag Master</div>
            <div className="text-sm text-slate-600">
              The dictionary that gives every fact a discoverable handle.
            </div>
            <div className="mt-4 mono-label flex items-center gap-1 text-slate-600 group-hover:gap-2 transition-all">
              Manage tags <CaretRight size={12} />
            </div>
          </Link>
          <Link to="/chat" className="block card-sharp p-6 group bg-slate-900 text-white border-slate-900" data-testid="quick-chat">
            <ChatCircle size={28} className="text-orange-400 mb-4" />
            <div className="font-display text-xl text-white mb-1">Talk to AiVerse</div>
            <div className="text-sm text-slate-300">
              Conversational retrieval, with citations to the exact flat.
            </div>
            <div className="mt-4 mono-label flex items-center gap-1 text-orange-400 group-hover:gap-2 transition-all">
              Open chat <CaretRight size={12} />
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
